package one.healthium.ui.components

import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import loco.GENERAL_FT_UNIT
import loco.GENERAL_IN_UNIT
import loco.Res
import one.healthium.base.utils.cmToFt
import one.healthium.base.utils.ftToIn
import one.healthium.base.utils.inToFt
import one.healthium.base.utils.toCm
import one.healthium.base.utils.toStringOrEmpty
import one.healthium.ui.compose.components.AppTextField
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_3
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds

@Composable
fun CmInput(
    modifier: Modifier = Modifier,
    value: Float?,
    onValueChanged: (Float?) -> Unit,
    showKeyboardAutomaticallyIfEmpty: Boolean = false,
    showKeyboardAutomatically: Boolean = false,
    showKeyboardDelay: Duration = 0.seconds,
) {
    fun displayValue() = value?.toInt().toStringOrEmpty()

    var textValue by remember {
        val text = displayValue()
        mutableStateOf(
            TextFieldValue(
                text = text,
                selection = TextRange(text.length),
            )
        )
    }
    LaunchedEffect(value) {
        textValue = textValue.copy(
            text = displayValue()
        )
    }

    AppTextField(
        modifier = modifier.fillMaxWidth(),
        value = textValue,
        onValueChange = { newValue ->
            textValue = newValue
            onValueChanged(
                if (newValue.text.isNotBlank()) {
                    newValue.text.toFloatOrNull() ?: value
                } else {
                    null
                }
            )
        },
        textStyle = AppTheme.textStyles.textField.copy(textAlign = TextAlign.Center),
        keyboardOptions = KeyboardOptions(
            keyboardType = KeyboardType.NumberPassword,
            imeAction = ImeAction.Done,
        ),
        maxLines = 1,
        showKeyboardAutomaticallyIfEmpty = showKeyboardAutomaticallyIfEmpty,
        showKeyboardAutomatically = showKeyboardAutomatically,
        showKeyboardDelay = showKeyboardDelay,
    )
}

@Composable
fun FtInput(
    modifier: Modifier = Modifier,
    value: Float?,
    isValid: (Float?) -> Boolean,
    onValueChanged: (Float?) -> Unit,
    unitColor: Color = gray_3,
    showKeyboardAutomaticallyIfEmpty: Boolean = false,
    showKeyboardAutomatically: Boolean = false,
    showKeyboardDelay: Duration = 0.seconds,
) {
    val ftFieldFocusRequester = remember { FocusRequester() }
    val inFieldFocusRequester = remember { FocusRequester() }

    val valueFt = value?.cmToFt() ?: 0f
    val valueIn = (valueFt - valueFt.toInt()).ftToIn()

    fun displayValueFt() = valueFt.toInt().toStringOrEmpty()
    fun displayValueIn() = valueIn.toStringOrEmpty()

    var textValueFt by remember {
        val text = displayValueFt()
        mutableStateOf(
            TextFieldValue(
                text = text,
                selection = TextRange(text.length),
            )
        )
    }

    var textValueIn by remember {
        val text = displayValueIn()
        mutableStateOf(
            TextFieldValue(
                text = text,
                selection = TextRange(text.length),
            )
        )
    }

    LaunchedEffect(value) {
        textValueFt = textValueFt.copy(
            text = displayValueFt()
        )
        textValueIn = textValueIn.copy(
            text = displayValueIn()
        )

        if (isValid(value) && textValueIn.text.isEmpty() && textValueFt.text.isNotEmpty()) {
            inFieldFocusRequester.requestFocus()
        }
    }

    Row(
        modifier = modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Spacer(Modifier.weight(0.5f))
        AppTextField(
            modifier = Modifier
                .weight(1f)
                .focusRequester(ftFieldFocusRequester),
            value = textValueFt,
            onValueChange = { newValue ->
                textValueFt = newValue
                val newValueFt = if (newValue.text.isEmpty()) {
                    null
                } else if (newValue.text.length <= 2) {
                    newValue.text.toFloatOrNull() ?: valueFt
                } else {
                    valueFt
                }
                onValueChanged(newValueFt?.plus(valueIn)?.toCm())
            },
            textStyle = AppTheme.textStyles.textField.copy(textAlign = TextAlign.Center),
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.NumberPassword,
                imeAction = ImeAction.Next,
            ),
            maxLines = 1,
            showKeyboardAutomaticallyIfEmpty = showKeyboardAutomaticallyIfEmpty,
            showKeyboardDelay = showKeyboardDelay,
        )
        Text(
            text = stringResource(Res.string.GENERAL_FT_UNIT),
            style = AppTheme.textStyles.textField,
            color = unitColor,
        )
        AppTextField(
            modifier = Modifier
                .weight(1f)
                .focusRequester(inFieldFocusRequester)
                .then(
                    if (textValueIn.text.isEmpty()) {
                        Modifier.onKeyEvent { keyEvent ->
                            if (keyEvent.key == Key.Backspace) {
                                ftFieldFocusRequester.requestFocus()
                                false
                            } else {
                                false
                            }
                        }
                    } else {
                        Modifier
                    }
                ),
            value = textValueIn,
            onValueChange = { newValue ->
                textValueIn = newValue
                val newValueIn: Float? = when (newValue.text.length) {
                    0 -> null
                    1 -> newValue.text.toIntOrNull()?.inToFt()
                    2 -> newValue.text.toIntOrNull()?.inToFt()
                    else -> valueIn.inToFt()
                }
                val newValueFt: Float = if (newValueIn != null) {
                    valueFt.toInt() + newValueIn
                } else {
                    valueFt.toInt().toFloat()
                }
                println("newValueIn: $newValueIn, newValueFt: $newValueFt")

                onValueChanged(newValueFt.toCm())
            },
            textStyle = AppTheme.textStyles.textField.copy(textAlign = TextAlign.Center),
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.NumberPassword,
                imeAction = ImeAction.Done,
            ),
            showKeyboardAutomatically = showKeyboardAutomatically && showKeyboardAutomaticallyIfEmpty.not(),
            showKeyboardDelay = showKeyboardDelay,
            maxLines = 1,
        )
        Text(
            text = stringResource(Res.string.GENERAL_IN_UNIT),
            style = AppTheme.textStyles.textField,
            color = unitColor,
        )
        Spacer(Modifier.weight(0.5f))
    }
}
